import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { apiFetch } from "./api";
import { useRestaurant } from "./RestaurantContext";

const Stats: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentRestaurantId, loading: restaurantLoading } = useRestaurant();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    // Wait for RestaurantContext's own fetch, but only when we truly have
    // nothing yet - see the matching comment in Reviews.tsx. A returning
    // user already has currentRestaurantId from localStorage synchronously.
    if ((user.role === 'admin' || user.role === 'manager') && restaurantLoading && !currentRestaurantId) return;
    setLoading(true);

    if (user.role === 'manager') {
      // ?restaurantId= names the currently switched-to location, same as
      // Reviews.tsx - a multi-location manager used to always see their
      // first assigned location's stats regardless of the switcher.
      const query = currentRestaurantId ? `?restaurantId=${currentRestaurantId}` : '';
      apiFetch(`/api/manager/stats${query}`)
        ?.then((res) => res?.json())
              .then((statsData) => {
                setStats(statsData);
                setLoading(false);
              })
              .catch(() => {
          setError(t('stats.failedToFetch'));
                setLoading(false);
              });
    } else if (user.role === 'admin') {
      // Fetch stats for the currently selected restaurant
      const adminRestaurantId = currentRestaurantId;
      if (!adminRestaurantId) {
        setError(t('stats.noRestaurantForAdmin'));
        setLoading(false);
        return;
      }
      apiFetch(`/api/restaurants/${adminRestaurantId}/stats`)
        ?.then((res) => {
          if (res?.status === 404) {
            setError(t('stats.noStatsForRestaurant'));
            setLoading(false);
            return null;
          }
          return res?.json();
        })
              .then((statsData) => {
          if (statsData) {
                setStats(statsData);
            setLoading(false);
          }
        })
        .catch(() => {
          setError(t('stats.failedToFetch'));
          setLoading(false);
        });
    }
  }, [t, currentRestaurantId, restaurantLoading]);

  if (loading) return <div className="p-4 text-center">{t('stats.loading')}</div>;
  if (error) return <div className="p-4 text-center text-red-600">{error}</div>;
  if (!stats) return null;

  return (
    <div className="p-6 max-w-md mx-auto">
      <h1 className="text-2xl font-bold mb-6 text-center">{t('stats.yourStats')}</h1>
      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <span className="text-3xl font-bold text-teal-600">{stats.totalReviews}</span>
          <span className="text-gray-500 mt-1">{t('stats.totalReviews')}</span>
        </div>
        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <span className="text-3xl font-bold text-indigo-600">{stats.totalReplies}</span>
          <span className="text-gray-500 mt-1">{t('stats.totalReplied')}</span>
        </div>
        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <span className="text-3xl font-bold text-green-600">{stats.myReplies}</span>
          <span className="text-gray-500 mt-1">{t('stats.repliedByYou')}</span>
        </div>
        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <span className="text-3xl font-bold text-purple-600">{stats.reviewsThisWeek}</span>
          <span className="text-gray-500 mt-1">{t('stats.reviewsThisWeek')}</span>
        </div>
        <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
          <span className="text-3xl font-bold text-red-600">{stats.pendingReplies}</span>
          <span className="text-gray-500 mt-1">{t('stats.pendingReplies')}</span>
        </div>
        {stats.totalReplies > 0 && (
          <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
            <span className="text-3xl font-bold text-teal-800">
              {stats.myReplyShare}%
            </span>
            <span className="text-gray-500 mt-1">{t('stats.yourShareOfReplies')}</span>
          </div>
        )}
        {stats.myAvgResponseTime !== null && (
          <div className="bg-white rounded-xl shadow p-6 flex flex-col items-center">
            <span className="text-3xl font-bold text-orange-600">{t('stats.days', { count: stats.myAvgResponseTime })}</span>
            <span className="text-gray-500 mt-1">{t('stats.avgResponseTime')}</span>
          </div>
        )}
        {/* Everyone else's contribution - "replied by you" above already
            covers the viewer's own count, this is the rest of the team
            (there can be more than one manager on a location). */}
        {stats.repliesByPerson && stats.repliesByPerson.length > 0 && (
          <div className="bg-white rounded-xl shadow p-6">
            <h2 className="font-semibold text-gray-700 mb-3 text-center">{t('stats.repliesByTeammate')}</h2>
            <div className="space-y-2">
              {stats.repliesByPerson.map((p: any) => (
                <div key={p.userId} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 truncate mr-2">{p.name || p.email}</span>
                  <span className="font-semibold text-gray-900 shrink-0">{p.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Stats; 